import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ChevronDown, Copy, GripVertical, Info, Video, X } from 'lucide-react'
import {
  EMPTY_OPTIONS,
  EXERCISE_PAIR_DEFS,
  getExercisePairField,
  getExercisePairValue,
  resolveExerciseInfoText,
  resolveExerciseVideoUrl,
  toFieldBadgeLabel,
} from '../utils/sessionDetailHelpers'

const SortableExerciseRow = React.memo(({
  planId,
  planApiId,
  planName,
  exercise,
  categories,
  nameOptions = EMPTY_OPTIONS,
  nameOptionsLoading = false,
  onFieldChange,
  onFieldCommit,
  onNewRowCategoryChange,
  onNewRowNameChange,
  onVideo,
  onCopy,
  onInfo,
  onDelete,
  disabled = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: exercise.id,
    data: {
      type: 'exerciseRow',
      planId,
      planApiId,
      planName,
      exerciseId: exercise.id,
      exerciseName: exercise.name || 'Exercise',
      catName: exercise.catName || '',
    },
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }
  const hasInfo = Boolean(resolveExerciseInfoText(exercise))
  const hasVideo = Boolean(resolveExerciseVideoUrl(exercise))
  const isNewRow = Boolean(exercise?.isNewRow)
  const pairDefs = EXERCISE_PAIR_DEFS

  const [pairDraft, setPairDraft] = useState({
    pair1: getExercisePairValue(exercise, 'sets', 'mode'),
    pair2: getExercisePairValue(exercise, 'reps', 'intensity'),
    pair3: getExercisePairValue(exercise, 'rest', 'heartrate'),
    pair4: getExercisePairValue(exercise, 'speed', 'duration'),
  })

  useEffect(() => {
    setPairDraft({
      pair1: getExercisePairValue(exercise, 'sets', 'mode'),
      pair2: getExercisePairValue(exercise, 'reps', 'intensity'),
      pair3: getExercisePairValue(exercise, 'rest', 'heartrate'),
      pair4: getExercisePairValue(exercise, 'speed', 'duration'),
    })
  }, [
    exercise.id,
    exercise.sets,
    exercise.mode,
    exercise.reps,
    exercise.intensity,
    exercise.rest,
    exercise.heartrate,
    exercise.speed,
    exercise.duration,
  ])

  const [nameDraft, setNameDraft] = useState(exercise.name || '')

  useEffect(() => {
    setNameDraft(exercise.name || '')
  }, [exercise.id, exercise.name])

  const [showOriginalNameMenu, setShowOriginalNameMenu] = useState(false)
  const [originalNameMenuPos, setOriginalNameMenuPos] = useState(null)
  const nameFieldRef = useRef(null)
  const nameMenuRef = useRef(null)

  const openOriginalNameMenu = useCallback(() => {
    if (!nameFieldRef.current) return
    const rect = nameFieldRef.current.getBoundingClientRect()
    setOriginalNameMenuPos({ top: rect.bottom + 4, left: rect.left, width: rect.width })
    setShowOriginalNameMenu(true)
  }, [])

  useEffect(() => {
    if (!showOriginalNameMenu) return undefined
    const close = () => setShowOriginalNameMenu(false)
    const handleClickOutside = (event) => {
      const target = event.target
      if (nameFieldRef.current?.contains(target)) return
      if (nameMenuRef.current?.contains(target)) return
      close()
    }
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [showOriginalNameMenu])

  const handleDraftChange = useCallback((field, value) => {
    setPairDraft((prev) => (prev[field] === value ? prev : { ...prev, [field]: value }))
  }, [])

  const commitField = useCallback((field, value) => {
    if (disabled) return
    const prevValue = exercise?.[field] ?? ''
    if (String(prevValue) === String(value)) return
    onFieldChange?.(planId, exercise.id, field, value)
    onFieldCommit?.(planId, exercise.id, field, value, prevValue)
  }, [disabled, exercise, onFieldChange, onFieldCommit, planId])

  const commitPair = useCallback((pairDef, value) => {
    if (disabled) return
    const field = getExercisePairField(exercise, pairDef.primary, pairDef.secondary)
    commitField(field, value)
  }, [commitField, disabled, exercise])

  const bodyPartLabel = exercise.catName || '-'
  const hasCategory = Boolean(exercise?.catId)
  const originalExerciseName = exercise.template?.name || ''
  const isCustomName = Boolean(originalExerciseName) && nameDraft.trim() && nameDraft.trim() !== originalExerciseName

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`transition-colors ${
        isDragging ? 'relative z-10 bg-white shadow-md ring-1 ring-fitness-primary/40' : 'hover:bg-slate-50/80'
      }`}
    >
      <td className="px-2 py-1.5">
        <button
          type="button"
          {...attributes}
          {...listeners}
          disabled={disabled}
          className="flex h-8 w-6 items-center justify-center rounded-md border border-slate-200 text-slate-400 transition hover:border-slate-300 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50 active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-3" />
        </button>
      </td>

      <td className="px-2 py-1.5">
        {isNewRow ? (
          <select
            value={exercise.catId || ''}
            onChange={(e) => onNewRowCategoryChange?.(planId, exercise.id, e.target.value)}
            disabled={disabled}
            className="w-full rounded-md border border-slate-200 bg-white px-2 py-2 text-[11px] font-medium text-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
          >
            <option value="">Select body part</option>
            {categories.map((opt) => (
              <option key={`${planId}-${exercise.id}-cat-${opt.id}`} value={opt.id}>
                {opt.name}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={bodyPartLabel}
            readOnly
            className="w-full cursor-not-allowed rounded-md border border-slate-200 bg-slate-50 px-2 py-2 text-[11px] font-medium text-slate-600"
          />
        )}
      </td>

      <td className="px-2 py-1.5">
        {isNewRow ? (
          <select
            value={exercise.templateId || ''}
            onChange={(e) => onNewRowNameChange?.(planId, exercise.id, e.target.value)}
            disabled={disabled || !hasCategory || nameOptionsLoading}
            className="w-full rounded-md border border-slate-200 bg-white px-2 py-2 text-[11px] font-medium text-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
          >
            <option value="">
              {!hasCategory ? 'Select body part first' : nameOptionsLoading ? 'Loading...' : 'Select exercise'}
            </option>
            {nameOptions.map((opt) => (
              <option key={`${planId}-${exercise.id}-name-${opt.id}`} value={opt.id}>
                {opt.name}
              </option>
            ))}
          </select>
        ) : (
          <div className="relative" ref={nameFieldRef}>
            <input
              type="text"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={(e) => commitField('name', e.target.value)}
              disabled={disabled}
              className={`w-full rounded-md border border-slate-200 px-2 py-2 text-[11px] font-medium text-slate-800 transition focus:border-fitness-primary focus:outline-none focus:ring-2 focus:ring-fitness-primary/20 disabled:cursor-not-allowed disabled:opacity-70 ${
                isCustomName ? 'pr-6' : ''
              }`}
            />
            {isCustomName ? (
              <button
                type="button"
                onClick={() => (showOriginalNameMenu ? setShowOriginalNameMenu(false) : openOriginalNameMenu())}
                disabled={disabled}
                title={`Original: ${originalExerciseName}`}
                className="absolute right-1 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-slate-400 transition hover:text-slate-700 disabled:cursor-not-allowed"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            ) : null}
            {isCustomName && showOriginalNameMenu && originalNameMenuPos && typeof document !== 'undefined'
              ? createPortal(
                  <div
                    ref={nameMenuRef}
                    style={{
                      position: 'fixed',
                      top: originalNameMenuPos.top,
                      left: originalNameMenuPos.left,
                      width: originalNameMenuPos.width,
                    }}
                    className="z-50 overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setNameDraft(originalExerciseName)
                        commitField('name', originalExerciseName)
                        setShowOriginalNameMenu(false)
                      }}
                      className="block w-full px-2 py-2 text-left text-[11px] font-medium text-slate-700 hover:bg-slate-50"
                    >
                      {originalExerciseName}
                    </button>
                  </div>,
                  document.body
                )
              : null}
          </div>
        )}
      </td>

      {pairDefs.map((pair) => {
        const activeField = getExercisePairField(exercise, pair.primary, pair.secondary)
        return (
          <td key={`${exercise.id}-${pair.key}`} className="px-2 py-1.5">
            <div className="relative">
              <input
                type="text"
                value={pairDraft[pair.key]}
                onChange={(e) => handleDraftChange(pair.key, e.target.value)}
                onBlur={(e) => commitPair(pair, e.target.value)}
                disabled={disabled || isNewRow}
                placeholder={pair.placeholder}
                className="w-full rounded-md border border-slate-200 px-2 py-2 pr-16 text-[11px] font-medium text-slate-800 placeholder:text-slate-400 disabled:cursor-not-allowed disabled:opacity-70"
              />
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                {toFieldBadgeLabel(activeField)}
              </span>
            </div>
          </td>
        )
      })}

      <td className="px-2 py-1.5">
        <div className="flex items-center justify-end gap-1 text-slate-500">
          <button
            type="button"
            onClick={() => onVideo?.(exercise)}
            disabled={!hasVideo || disabled}
            className="rounded-md border border-slate-200 p-2 text-slate-700 transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
            title={hasVideo ? 'Watch video' : 'No video available'}
          >
            <Video className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onCopy?.(planId, exercise)}
            disabled={disabled || !exercise?.apiId}
            className="rounded-md border border-slate-200 p-2 text-slate-700 transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
            title="Copy exercise"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onInfo?.(exercise)}
            disabled={!hasInfo || disabled}
            className="rounded-md border border-slate-200 p-2 text-slate-700 transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
            title={hasInfo ? 'More info' : 'No additional info'}
          >
            <Info className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onDelete?.(planId, planApiId, planName, exercise)}
            disabled={disabled}
            className="rounded-md border border-red-200 p-2 text-red-500 transition hover:border-red-300 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
            title="Delete exercise"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  )
}, (prev, next) =>
  prev.planId === next.planId &&
  prev.planApiId === next.planApiId &&
  prev.planName === next.planName &&
  prev.exercise === next.exercise &&
  prev.categories === next.categories &&
  prev.nameOptions === next.nameOptions &&
  prev.nameOptionsLoading === next.nameOptionsLoading &&
  prev.onFieldChange === next.onFieldChange &&
  prev.onFieldCommit === next.onFieldCommit &&
  prev.onNewRowCategoryChange === next.onNewRowCategoryChange &&
  prev.onNewRowNameChange === next.onNewRowNameChange &&
  prev.onVideo === next.onVideo &&
  prev.onCopy === next.onCopy &&
  prev.onInfo === next.onInfo &&
  prev.onDelete === next.onDelete &&
  prev.disabled === next.disabled
)

export default SortableExerciseRow
